/**
 * API Keys Service
 *
 * Service for handling API key management including
 * CRUD operations, revocation, and scope queries.
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

// API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:14102/api/v1';

// Token storage keys (shared with authService)
const ACCESS_TOKEN_KEY = 'access_token';

/**
 * API Key interface
 */
export interface ApiKey {
  id: number;
  name: string;
  prefix: string;
  scopes: string[];
  status: 'active' | 'revoked' | 'expired';
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * API Key creation response (includes full key only once)
 */
export interface ApiKeyCreateResponse {
  id: number;
  name: string;
  key: string;
  prefix: string;
  scopes: string[];
  status: 'active';
  expires_at: string | null;
  created_at: string;
}

/**
 * Create API key DTO
 */
export interface CreateApiKeyDto {
  name: string;
  scopes: string[];
  expires_at?: string;
}

/**
 * Update API key DTO
 */
export interface UpdateApiKeyDto {
  name?: string;
  scopes?: string[];
}

/**
 * Available scopes response
 */
export interface AvailableScope {
  name: string;
  description: string;
}

/**
 * In-memory token storage
 * Initialize from localStorage to persist across page refreshes
 */
let accessToken: string | null = localStorage.getItem(ACCESS_TOKEN_KEY);

/**
 * Create Axios instance with auth interceptors
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  // Request interceptor - add access token to requests
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = accessToken || localStorage.getItem(ACCESS_TOKEN_KEY);
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
        // Sync in-memory token if it was retrieved from localStorage
        if (!accessToken && token) {
          accessToken = token;
        }
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor - handle 401 errors by re-syncing token from localStorage
  // Token refresh is handled by authService's interceptor; this avoids race conditions
  // from multiple independent refresh attempts consuming the rotating refresh token.
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Re-read token from localStorage (authService may have refreshed it)
        const currentToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (currentToken && currentToken !== accessToken) {
          accessToken = currentToken;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${currentToken}`;
          }
          return client(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Create the API client instance
const apiClient = createApiClient();

/**
 * API Keys Service
 * Provides API key management methods
 */
export const apiKeysService = {
  /**
   * List all API keys for the current user
   */
  async getApiKeys(): Promise<ApiKey[]> {
    const response = await apiClient.get<ApiKey[]>('/api-keys');
    return response.data;
  },

  /**
   * Get a single API key by ID
   */
  async getApiKey(id: number): Promise<ApiKey> {
    const response = await apiClient.get<ApiKey>(`/api-keys/${id}`);
    return response.data;
  },

  /**
   * Create a new API key
   * Returns the full key value only once in the response
   */
  async createApiKey(data: CreateApiKeyDto): Promise<ApiKeyCreateResponse> {
    const response = await apiClient.post<ApiKeyCreateResponse>('/api-keys', data);
    return response.data;
  },

  /**
   * Update an existing API key (name and/or scopes)
   */
  async updateApiKey(id: number, data: UpdateApiKeyDto): Promise<ApiKey> {
    const response = await apiClient.patch<ApiKey>(`/api-keys/${id}`, data);
    return response.data;
  },

  /**
   * Revoke (delete) an API key
   */
  async revokeApiKey(id: number): Promise<void> {
    await apiClient.delete(`/api-keys/${id}`);
  },

  /**
   * Get available scopes for API keys
   */
  async getAvailableScopes(): Promise<AvailableScope[]> {
    const response = await apiClient.get<AvailableScope[]>('/api-keys/scopes');
    return response.data;
  },

  /**
   * Set access token (for synchronization with authService)
   */
  setAccessToken(token: string): void {
    accessToken = token;
  },

  /**
   * Clear access token
   */
  clearAccessToken(): void {
    accessToken = null;
  },
};

export default apiKeysService;
